import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  addDoc,
  serverTimestamp,
  writeBatch,
  increment,
  onSnapshot
} from 'firebase/firestore';
import { db, auth } from '../firebase';

// Service-level caching
const _revisionCache = new Map();
const _publishedCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes TTL

// User Management
export const userService = {
  // Get user by UID
  getUser: async (uid) => {
    const userDoc = await getDoc(doc(db, 'users', uid));
    return userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : null;
  },

  // Create/update user
  saveUser: async (uid, userData) => {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, {
      ...userData,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { id: uid, ...userData };
  },

  // Update user
  updateUser: async (uid, updateData) => {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
  },

  // Custom Categories Management
  getCustomCategories: async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) return [];
      return userDoc.data()?.customCategories || [];
    } catch (err) {
      console.error("Error getting custom categories:", err);
      return [];
    }
  },

  addCustomCategory: async (uid, categoryName) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      const existing = userDoc.data()?.customCategories || [];
      
      // Check for duplicates (case-insensitive)
      if (existing.some(c => c.name.toLowerCase() === categoryName.toLowerCase().trim())) {
        throw new Error("Category already exists");
      }
      
      const newCategory = {
        id: Date.now().toString(),
        name: categoryName.trim(),
        createdAt: new Date().toISOString()
      };
      
      await updateDoc(doc(db, 'users', uid), {
        customCategories: [...existing, newCategory],
        updatedAt: serverTimestamp()
      });
      
      return newCategory;
    } catch (err) {
      console.error("Error adding custom category:", err);
      throw err;
    }
  },

  deleteCustomCategory: async (uid, categoryId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      const existing = userDoc.data()?.customCategories || [];
      const updated = existing.filter(c => c.id !== categoryId);
      
      await updateDoc(doc(db, 'users', uid), {
        customCategories: updated,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (err) {
      console.error("Error deleting custom category:", err);
      throw err;
    }
  },

  // Get users by account type (admin only)
  getUsersByAccountType: async (accountType) => {
    const q = query(
      collection(db, 'users'),
      where('accountType', '==', accountType)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Get user by email
  getUserByEmail: async (email) => {
    const normalizedEmail = email.toLowerCase().trim();
    
    // Use the same approach as checkEmailExists since that works for mobile accounts
    const q = query(
      collection(db, 'users'),
      where('email', '==', normalizedEmail)
    );
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    }
    
    return null;
  },

  // Check if email exists
  checkEmailExists: async (email) => {
    const q = query(
      collection(db, 'users'),
      where('email', '==', email.toLowerCase().trim())
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  },


  // Check if phone exists
  // NOTE: Performs parallel queries on both phone field paths for compatibility.
  // Worst case: 2 read operations. Consider normalizing to single canonical field for cost optimization.
  // 
  // SCHEMA NORMALIZATION RECOMMENDATION:
  // To reduce read costs from 2 to 1, consider:
  // 1. Normalize phone storage to ONE canonical field at write time (e.g., always use 'phone')
  // 2. Use a compound phoneNumbers array with array-contains query
  // 3. Add a migration script to update existing documents to use canonical field
  checkPhoneExists: async (phone) => {
    // Run both queries in parallel to reduce latency
    const q1 = query(
      collection(db, 'users'),
      where('phone', '==', phone)
    );
    const q2 = query(
      collection(db, 'users'),
      where('contactDetails.contact', '==', phone)
    );
    
    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2)
    ]);
    
    // Return true if phone exists in either field
    return !snapshot1.empty || !snapshot2.empty;
  },

  // Get user by phone number
  getUserByPhone: async (phone) => {
    // First try root-level phone field
    const q1 = query(
      collection(db, 'users'),
      where('phone', '==', phone)
    );
    const snapshot1 = await getDocs(q1);
    
    if (!snapshot1.empty) {
      return { id: snapshot1.docs[0].id, ...snapshot1.docs[0].data() };
    }
    
    // If not found, try contactDetails.contact field
    const q2 = query(
      collection(db, 'users'),
      where('contactDetails.contact', '==', phone)
    );
    const snapshot2 = await getDocs(q2);
    
    if (!snapshot2.empty) {
      return { id: snapshot2.docs[0].id, ...snapshot2.docs[0].data() };
    }
    
    return null;
  },

  // OTP helpers for registration
  generateAndSaveOtp: async (uid, otp) => {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      registrationOtp: otp,
      registrationOtpExpiresAt: new Date(Date.now() + 60 * 1000),
      registrationOtpSentAt: new Date(),
      registrationOtpVerified: false,
      updatedAt: serverTimestamp()
    });
  },

  verifyOtp: async (uid, code) => {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (!userDoc.exists()) {
      return { success: false, error: 'User not found' };
    }
    const data = userDoc.data();

    if (!data.registrationOtp || !data.registrationOtpExpiresAt) {
      return { success: false, error: 'No OTP has been generated. Request a new code.' };
    }

    let expiresAt = data.registrationOtpExpiresAt;
    if (expiresAt?.toDate) {
      expiresAt = expiresAt.toDate();
    }
    if (!(expiresAt instanceof Date)) {
      expiresAt = new Date(expiresAt);
    }

    if (expiresAt < new Date()) {
      return { success: false, error: 'OTP has expired. Request a new code.' };
    }

    if (String(code) !== String(data.registrationOtp)) {
      return { success: false, error: 'Incorrect code, please try again' };
    }

    await updateDoc(doc(db, 'users', uid), {
      registrationOtpVerified: true,
      registrationStep: 9,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  },

  // End OTP helpers

  // Data transformation functions for mobile-web compatibility
  isGroupedStructure: (userData) => {
    return !!(userData && (
      userData.personalInformation || 
      userData.contactDetails || 
      userData.accountSetup ||
      userData.guardianContactDetails ||
      userData.accompanistContactDetails ||
      userData.assistance
    ));
  },

  // Helper function to determine if account is minor
  isMinorAccount: (flatData) => {
    const reasonForAssistance = flatData.reasonForAssistance || [];
    return Array.isArray(reasonForAssistance)
      ? reasonForAssistance.includes("minor")
      : reasonForAssistance === "minor";
  },

  prepareGroupedUserData: (flatData) => {
    const groupedData = {
      // Keep auth fields at root level for compatibility with Mobile App backend
      email: flatData.email,
      password: flatData.password || '',
      phone: flatData.phone || flatData.contact || '',
      phoneLast10: flatData.phoneLast10 || flatData.contact || '',
      accountType: flatData.accountType || "User",
      registrationStep: flatData.registrationStep,
      verified: flatData.verified,
      createdAt: flatData.createdAt,
      updatedAt: flatData.updatedAt,
      twoFactorEnabled: flatData.twoFactorEnabled !== undefined ? flatData.twoFactorEnabled : true,
      
      // Personal information at root level for Mobile App compatibility
      firstName: flatData.firstName || '',
      lastName: flatData.lastName || '',
      sex: flatData.sex || '',
      dob: flatData.dob || '',
      
      // Geographic data at root level for Mobile App compatibility
      region: flatData.region || '',
      province: flatData.province || '',
      city: flatData.city || '',
      barangay: flatData.barangay || '',

      // Group personal information data
      personalInformation: {
        firstName: flatData.firstName || '',
        lastName: flatData.lastName || '',
        sex: flatData.sex || '',
        dob: flatData.dob || '',
      },
      
      // Group contact details data
      contactDetails: {
        email: flatData.email,
        contact: flatData.contact || flatData.phone || '',
        region: flatData.region || '',
        province: flatData.province || '',
        city: flatData.city || '',
        barangay: flatData.barangay || ''
      },
      
      // Group account setup data
      accountSetup: {
        password: flatData.password || '',
        uniqueIdentifierCode: flatData.uniqueIdentifierCode || ''
      },
      
      // Group notification settings
      notificationSettings: flatData.notificationSettings || {
        educational: true,
        events: true,
        announcements: true,
        appointments: true,
        dataPrivacy: true,
      },
      
      // Group notification timestamps
      notificationTimestamps: flatData.notificationTimestamps || {
        educationalEnabledAt: serverTimestamp(),
        eventsEnabledAt: serverTimestamp(),
        announcementsEnabledAt: serverTimestamp(),
        appointmentsEnabledAt: serverTimestamp(),
        dataPrivacyEnabledAt: serverTimestamp(),
      },
      
      // Only include guardian details for minors
      ...(flatData.guardianFullName && userService.isMinorAccount(flatData) && {
        guardianContactDetails: {
          guardianFullName: flatData.guardianFullName,
          guardianRelationshipToUser: flatData.guardianRelationshipToUser,
          guardianContactNumber: flatData.guardianContactNumber,
          guardianAddress: flatData.guardianAddress || ""
        }
      }),
      
      // Only include accompanist details for non-minors needing accompaniment
      ...(flatData.accompanistFullName && !userService.isMinorAccount(flatData) && {
        accompanistContactDetails: {
          accompanistFullName: flatData.accompanistFullName,
          accompanistRelationshipToUser: flatData.accompanistRelationshipToUser,
          accompanistContactNumber: flatData.accompanistContactNumber,
          accompanistAddress: flatData.accompanistAddress || ""
        }
      }),
      
      // Only include assistance if it exists
      ...(flatData.assistanceRequired && {
        assistance: {
          assistanceRequired: flatData.assistanceRequired,
          reasonForAssistance: flatData.reasonForAssistance,
          reasonText: flatData.reasonText || '',
          disabilityText: flatData.disabilityText || ''
        }
      })
    };

    if (flatData.emergencyContact) {
      groupedData.emergencyContact = flatData.emergencyContact;
    }
    if (flatData.selectedTopics) {
      groupedData.selectedTopics = flatData.selectedTopics;
    }

    return groupedData;
  },

  extractUserData: (groupedData) => {
    // Helper function to normalize date format
    const normalizeDate = (dateStr) => {
      if (!dateStr) return '';
      
      // If already in YYYY-MM-DD format, return as-is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      
      // Convert MM/DD/YYYY to YYYY-MM-DD format
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
        const [month, day, year] = dateStr.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      
      return dateStr;
    };

    return {
      // Personal information - check both nested and root structures
      firstName: groupedData.personalInformation?.firstName || groupedData.firstName || '',
      lastName: groupedData.personalInformation?.lastName || groupedData.lastName || '',
      sex: groupedData.personalInformation?.sex || groupedData.sex || '',
      dob: normalizeDate(groupedData.personalInformation?.dob || groupedData.dob || ''),
      
      // Geographic details - check both nested and root structures
      province: groupedData.contactDetails?.province || groupedData.province || '',
      city: groupedData.contactDetails?.city || groupedData.city || '',
      barangay: groupedData.contactDetails?.barangay || groupedData.barangay || '',
      region: groupedData.contactDetails?.region || groupedData.region || '',
      
      // Contact details
      email: groupedData.contactDetails?.email || groupedData.email || '',
      contact: groupedData.contactDetails?.contact || groupedData.contact || groupedData.phone || '',
      
      // Account setup
      password: groupedData.accountSetup?.password || groupedData.password || '',
      uniqueIdentifierCode: groupedData.accountSetup?.uniqueIdentifierCode || groupedData.uniqueIdentifierCode || '',
      
      // Guardian details (if available)
      guardianFullName: groupedData.guardianContactDetails?.guardianFullName || '',
      guardianRelationshipToUser: groupedData.guardianContactDetails?.guardianRelationshipToUser || '',
      guardianContactNumber: groupedData.guardianContactDetails?.guardianContactNumber || '',
      guardianAddress: groupedData.guardianContactDetails?.guardianAddress || '',
      
      // Accompanist details (if available)
      accompanistFullName: groupedData.accompanistContactDetails?.accompanistFullName || '',
      accompanistRelationshipToUser: groupedData.accompanistContactDetails?.accompanistRelationshipToUser || '',
      accompanistContactNumber: groupedData.accompanistContactDetails?.accompanistContactNumber || '',
      accompanistAddress: groupedData.accompanistContactDetails?.accompanistAddress || '',
      
      // Assistance information - check both nested and flat structures
      assistanceRequired: groupedData.assistance?.assistanceRequired || groupedData.assistanceRequired || '',
      reasonForAssistance: groupedData.assistance?.reasonForAssistance || groupedData.reasonForAssistance || [],
      reasonText: groupedData.assistance?.reasonText || groupedData.reasonText || '',
      disabilityText: groupedData.assistance?.disabilityText || groupedData.disabilityText || '',
      
      emergencyContact: groupedData.emergencyContact || {},
      
      // Metadata
      currentStep: groupedData.currentStep || '',
      registrationCompleted: groupedData.registrationCompleted || false,
      
      // Root level fields (for auth compatibility)
      phone: groupedData.phone || '',
      phoneLast10: groupedData.phoneLast10 || '',
      accountType: groupedData.accountType || 'User',
      verified: groupedData.verified || false,
      
      // Health information fields (stored at root level)
      medicalHistory: groupedData.medicalHistory || [],
      medicalOther: groupedData.medicalOther || '',
      allergies: groupedData.allergies || [],
      allergyOther: groupedData.allergyOther || '',
      hormonalMedications: groupedData.hormonalMedications || groupedData.hormonalAllergyDetails || '',
      hormonalAllergyDetails: groupedData.hormonalAllergyDetails || '',
      smoking: groupedData.smoking || '',
      alcoholUse: groupedData.alcoholUse || '',
      stiHistory: groupedData.stiHistory || [],
      stiOther: groupedData.stiOther || '',
      hivStatus: groupedData.hivStatus || '',
      hivOther: groupedData.hivOther || '',
      lastTestedDate: groupedData.lastTestedDate || '',
      genitalInfections: groupedData.genitalInfections || '',
      additionalHealthHistory: groupedData.additionalHealthHistory || groupedData.hormonalContraception || [],
      hormonalContraception: groupedData.hormonalContraception || [],
      contraceptionOther: groupedData.contraceptionOther || groupedData.hormonalContraceptionOther || '',
      hormonalContraceptionOther: groupedData.hormonalContraceptionOther || '',
      pastSurgeries: groupedData.pastSurgeries || [],
      surgeryOther: groupedData.surgeryOther || groupedData.pastSurgeryOther || '',
      pastSurgeryOther: groupedData.pastSurgeryOther || '',
      currentMedications: groupedData.currentMedications || '',
      emergencyContactName: groupedData.emergencyContactName || '',
      emergencyContactRelationship: groupedData.emergencyContactRelationship || '',
      emergencyContactRelationshipOther: groupedData.emergencyContactRelationshipOther || '',
      emergencyContactNumber: groupedData.emergencyContactNumber || ''
    };
  },

  saveUserWithGroupedStructure: async (uid, flatData) => {
    const groupedData = userService.prepareGroupedUserData(flatData);
    return await userService.saveUser(uid, groupedData);
  }
};

// Approval Management
export const approvalService = {
  // Get all approvals (all statuses)
  getAllApprovals: async () => {
    try {
      const q = query(
        collection(db, 'approvals'),
        orderBy('submittedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      return result;
    } catch (error) {
      console.error('getAllApprovals: Error fetching approvals:', error);
      throw error;
    }
  },

  // Get paginated approvals with lazy loading
  getPaginatedApprovals: async (pageSize = 100, lastDoc = null) => {
    try {
      let q = query(
        collection(db, 'approvals'),
        orderBy('submittedAt', 'desc'),
        limit(pageSize)
      );
      
      if (lastDoc) {
        q = query(
          collection(db, 'approvals'),
          orderBy('submittedAt', 'desc'),
          startAfter(lastDoc),
          limit(pageSize)
        );
      }
      
      const snapshot = await getDocs(q);
      const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const lastVisible = snapshot.docs[snapshot.docs.length - 1];
      
      return {
        data: result,
        lastDoc: lastVisible,
        hasMore: result.length === pageSize
      };
    } catch (error) {
      console.error('getPaginatedApprovals: Error fetching paginated approvals:', error);
      throw error;
    }
  },

  // Get all pending approvals
  getPendingApprovals: async () => {
    const q = query(
      collection(db, 'approvals'),
      where('status', '==', 'Pending'),
      orderBy('submittedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Get approval by ID
  getApprovalById: async (id) => {
    const approvalDoc = await getDoc(doc(db, 'approvals', id));
    return approvalDoc.exists() ? { id: approvalDoc.id, ...approvalDoc.data() } : null;
  },

  // Get approval by contentId
  getApprovalByContentId: async (contentId) => {
    const q = query(
      collection(db, 'approvals'),
      where('contentId', '==', contentId)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.docs.length > 0) {
      const approvalDoc = snapshot.docs[0];
      return { id: approvalDoc.id, ...approvalDoc.data() };
    }
    
    return null;
  },

  // Submit content for approval
  submitForApproval: async (contentData) => {
    const docRef = await addDoc(collection(db, 'approvals'), {
      ...contentData,
      status: 'Pending',
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { id: docRef.id, ...contentData };
  },

  // Update approval status
  updateApprovalStatus: async (id, status, reviewData = {}) => {
    const approvalRef = doc(db, 'approvals', id);
    const updateData = {
      status,
      updatedAt: serverTimestamp(),
      ...reviewData
    };
    
    await updateDoc(approvalRef, updateData);
    
    if (status === 'Approved') {
      // Get the approval data to copy to published collection
      const approvalDoc = await getDoc(doc(db, 'approvals', id));
      if (approvalDoc.exists()) {
        const approvalData = { id: approvalDoc.id, ...approvalDoc.data() };
        await publishedService.addToPublished(approvalData);
        
        // Trigger notifications + emails via Cloud Function (uses admin SDK,
        // bypasses Firestore security rules so it can write to any user's
        // notifications subcollection)
        try {
          const functionsUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-auravit-bb843.cloudfunctions.net';
          const response = await fetch(`${functionsUrl}/createContentNotifications`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contentId: approvalData.contentId,
              title: approvalData.title,
              category: approvalData.category,
              socialImageUrl: approvalData.socialImageUrl || '',
              minorEligible: approvalData.minorEligible === true,
            }),
          });
          if (!response.ok) {
            const errBody = await response.json().catch(() => ({}));
            console.error('updateApprovalStatus: createContentNotifications CF error:', errBody);
          } else {
            const result = await response.json();
            console.log('updateApprovalStatus: notifications/emails sent:', result);
          }
        } catch (notifError) {
          console.error('updateApprovalStatus: Failed to call createContentNotifications:', notifError);
          // Don't fail the approval if notifications fail
        }
      }
    } else if (status === 'Rejected') {
      // Get the approval data to remove from published collection
      const approvalDoc = await getDoc(doc(db, 'approvals', id));
      if (approvalDoc.exists()) {
        const approvalData = { id: approvalDoc.id, ...approvalDoc.data() };
        if (approvalData.contentId) {
          await publishedService.removeFromPublished(approvalData.contentId);
        }
      }
    }
    
    return { id, status, ...updateData };
  },

  // Update approval (general update)
  updateApproval: async (id, updateData) => {

    const approvalRef = doc(db, 'approvals', id);
    const data = {
      ...updateData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(approvalRef, data);

    
    return { id, ...data };
  },

  // Delete approval
  deleteApproval: async (id) => {

    const approvalRef = doc(db, 'approvals', id);
    
    // First get the approval data to check if we need to remove from published
    const approvalDoc = await getDoc(approvalRef);
    if (approvalDoc.exists()) {
      const approvalData = { id: approvalDoc.id, ...approvalDoc.data() };
      if (approvalData.contentId) {
        await publishedService.removeFromPublished(approvalData.contentId);
      }
    }
    
    await deleteDoc(approvalRef);

    
    return { id };
  },

  // Copy approved content to public collection
  copyToPublicCollection: async (approvalId) => {
    try {

      const approvalDoc = await getDoc(doc(db, 'approvals', approvalId));
      if (!approvalDoc.exists()) {

        return;
      }
      
      const approvalData = approvalDoc.data();

      
      // Prepare public content data
      const publicContent = {
        title: approvalData.title,
        subtitle: approvalData.subtitle,
        bodyHtml: approvalData.bodyHtml,
        category: approvalData.category,
        tags: approvalData.tags || [],
        socialTitle: approvalData.socialTitle,
        socialDescription: approvalData.socialDescription,
        socialImageUrl: approvalData.socialImageUrl,
        author: approvalData.author,
        authorEmail: approvalData.authorEmail,
        authorRole: approvalData.authorRole,
        approvedAt: approvalData.approvedAt,
        approvedBy: approvalData.approvedBy,
        contentId: approvalData.contentId,
        createdAt: serverTimestamp(),
        status: 'Published'
      };
      

      
      // Add to contents collection (should have public read permissions)
      const docId = approvalId; // Use the approval ID as the document ID to prevent overwrites

      await setDoc(doc(db, 'contents', docId), publicContent);

      
    } catch (error) {
      console.error('copyToPublicCollection: Error copying to contents collection:', error);
    }
  },



  // Get approvals by status
  getApprovalsByStatus: async (status) => {
    const q = query(
      collection(db, 'approvals'),
      where('status', '==', status),
      orderBy('submittedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }
};

// Content Management
export const contentService = {
  // Get all content
  getAllContent: async () => {
    const q = query(
      collection(db, 'contents'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Get published content
  getPublishedContent: async () => {

    const q = query(
      collection(db, 'contents'),
      where('status', '==', 'Published'),
      orderBy('approvedAt', 'desc')
    );
    const snapshot = await getDocs(q);

    

    
    const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    return result;
  },

  // Save draft to approvals collection
  saveDraft: async (contentData) => {

    const docRef = await addDoc(collection(db, 'approvals'), {
      ...contentData,
      status: 'draft',
      submittedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { id: docRef.id, ...contentData };
  },

  // Get user-specific approvals (drafts and published content)
  getUserApprovals: async (userId) => {
    const q = query(
      collection(db, 'approvals'),
      where('authorId', '==', userId),
      orderBy('submittedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    return result;
  },

  // Get paginated user approvals with lazy loading
  getPaginatedUserApprovals: async (userId, pageSize = 100, lastDoc = null) => {
    try {
      let q = query(
        collection(db, 'approvals'),
        where('authorId', '==', userId),
        orderBy('submittedAt', 'desc'),
        limit(pageSize)
      );
      
      if (lastDoc) {
        q = query(
          collection(db, 'approvals'),
          where('authorId', '==', userId),
          orderBy('submittedAt', 'desc'),
          startAfter(lastDoc),
          limit(pageSize)
        );
      }
      
      const snapshot = await getDocs(q);
      const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const lastVisible = snapshot.docs[snapshot.docs.length - 1];
      
      return {
        data: result,
        lastDoc: lastVisible,
        hasMore: result.length === pageSize
      };
    } catch (error) {
      console.error('getPaginatedUserApprovals: Error fetching paginated user approvals:', error);
      throw error;
    }
  },

  // Get content by ID
  getContentById: async (id) => {
    const contentDoc = await getDoc(doc(db, 'contents', id));
    return contentDoc.exists() ? { id: contentDoc.id, ...contentDoc.data() } : null;
  },

  // Create content
  createContent: async (contentData) => {
    const docRef = await addDoc(collection(db, 'contents'), {
      ...contentData,
      createdAt: serverTimestamp(),
      submittedAt: serverTimestamp(), // Track when content was submitted for approval
      updatedAt: serverTimestamp()
    });
    return { id: docRef.id, ...contentData };
  },

  // Update content
  updateContent: async (id, updateData) => {
    const contentRef = doc(db, 'contents', id);
    await updateDoc(contentRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
  },

  // Delete content
  deleteContent: async (id) => {
    await deleteDoc(doc(db, 'contents', id));
  },

  // Get content by category
  getContentByCategory: async (category) => {
    const q = query(
      collection(db, 'contents'),
      where('category', '==', category),
      where('status', '==', 'Published'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }
};

// PSGC Geographic Data
const psgcCache = {
  regions: null,
  provincesByRegion: new Map(),
  citiesByProvince: new Map(),
  barangaysByCity: new Map(),
};

export const psgcService = {
  // Get all regions
  getRegions: async () => {
    if (psgcCache.regions) return psgcCache.regions;
    try {
      const response = await fetch('/psgc/regions.json');
      const regions = await response.json();
      regions.sort((a, b) => a.name.localeCompare(b.name));
      psgcCache.regions = regions;
      return regions;
    } catch (error) {
      console.error('Failed to fetch regions:', error);
      return [];
    }
  },

  // Get provinces by region
  getProvincesByRegion: async (regionPsgcId) => {
    if (!regionPsgcId) return [];
    if (psgcCache.provincesByRegion.has(regionPsgcId)) {
      return psgcCache.provincesByRegion.get(regionPsgcId);
    }

    try {
      const response = await fetch('/psgc/provinces.json');
      const allProvinces = await response.json();
      const filtered = allProvinces.filter(p => p.region_psgc_id === regionPsgcId);
      filtered.sort((a, b) => a.name.localeCompare(b.name));
      psgcCache.provincesByRegion.set(regionPsgcId, filtered);
      return filtered;
    } catch (error) {
      console.error('Failed to fetch provinces:', error);
      return [];
    }
  },

  // Get cities/municipalities by province
  getCitiesByProvince: async (provincePsgcId) => {
    if (!provincePsgcId) return [];
    if (psgcCache.citiesByProvince.has(provincePsgcId)) {
      return psgcCache.citiesByProvince.get(provincePsgcId);
    }

    try {
      const response = await fetch('/psgc/cities.json');
      const allCities = await response.json();
      const filtered = allCities.filter(c => c.province_psgc_id === provincePsgcId);
      filtered.sort((a, b) => a.name.localeCompare(b.name));
      psgcCache.citiesByProvince.set(provincePsgcId, filtered);
      return filtered;
    } catch (error) {
      console.error('Failed to fetch cities:', error);
      return [];
    }
  },

  // Get barangays by city
  getBarangaysByCity: async (cityPsgcId) => {
    if (!cityPsgcId) return [];
    if (psgcCache.barangaysByCity.has(cityPsgcId)) {
      return psgcCache.barangaysByCity.get(cityPsgcId);
    }

    try {
      const response = await fetch('/psgc/barangays.json');
      const allBarangays = await response.json();
      const filtered = allBarangays.filter(b => b.city_psgc_id === cityPsgcId);
      filtered.sort((a, b) => a.name.localeCompare(b.name));
      psgcCache.barangaysByCity.set(cityPsgcId, filtered);
      return filtered;
    } catch (error) {
      console.error('Failed to fetch barangays:', error);
      return [];
    }
  }
};

// Published Content Service
// Module-level cache for getAllPublished — avoids re-reading 100 docs on every navigation
let _allPublishedCache = null;
let _allPublishedCacheTime = 0;
const ALL_PUBLISHED_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const ALL_PUBLISHED_LS_KEY = 'auravit_published_cache';
const ALL_PUBLISHED_LS_TTL = 30 * 60 * 1000; // 30 minutes for localStorage

export const publishedService = {
  // Invalidate the getAllPublished cache so the next call fetches fresh stats.
  // Call this after any bookmark/view/share increment so navigating back to
  // the Learn page shows the updated counts instead of stale cached counts.
  invalidateAllPublishedCache: () => {
    _allPublishedCache = null;
    _allPublishedCacheTime = 0;
    try { localStorage.removeItem(ALL_PUBLISHED_LS_KEY); } catch (_) {}
  },

  getAllPublished: async (forceRefresh = false) => {
    const now = Date.now();

    // 1. Module-level memory cache (cleared on refresh)
    if (
      !forceRefresh &&
      _allPublishedCache &&
      (now - _allPublishedCacheTime) < ALL_PUBLISHED_CACHE_TTL
    ) {
      return _allPublishedCache;
    }

    // 2. localStorage cache — survives page refresh
    if (!forceRefresh) {
      try {
        const stored = localStorage.getItem(ALL_PUBLISHED_LS_KEY);
        if (stored) {
          const { data, timestamp } = JSON.parse(stored);
          const age = now - timestamp;
          if (age < ALL_PUBLISHED_LS_TTL && Array.isArray(data) && data.length > 0) {
            // Serve stale cache immediately, then refresh in background
            _allPublishedCache = data;
            _allPublishedCacheTime = timestamp;
            // Background refresh — don't await so caller gets data immediately
            publishedService.getAllPublished(true).catch(() => {});
            return data;
          }
        }
      } catch (_) {
        // localStorage unavailable or corrupted — fall through to network
      }
    }

    // 3. Network fetch
    const q = query(
      collection(db, 'published'),
      orderBy('publishedAt', 'desc'),
      limit(100)
    );
    const snapshot = await getDocs(q);
    
    const result = snapshot.docs.map(doc => {
      const { bodyHtml, ...rest } = doc.data();
      return { id: doc.id, ...rest };
    });

    // Update both caches
    _allPublishedCache = result;
    _allPublishedCacheTime = now;
    try {
      localStorage.setItem(ALL_PUBLISHED_LS_KEY, JSON.stringify({ data: result, timestamp: now }));
    } catch (_) {
      // localStorage quota exceeded — memory cache is still valid
    }

    return result;
  },
  
  // Real-time listener for all published content
  getAllPublishedListener: (callback) => {
    const q = query(
      collection(db, 'published'),
      orderBy('publishedAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(result);
    }, (error) => {
      console.error('publishedService: Error in real-time listener:', error);
    });
    
    return unsubscribe;
  },

  // Get published content by contentId
  getPublishedByContentId: async (contentId) => {
    // Check cache first
    if (_publishedCache.has(contentId)) {
      const cached = _publishedCache.get(contentId);
      if (Date.now() - cached.timestamp < CACHE_TTL) {
        console.log('publishedService: Using cached published content for contentId:', contentId);
        return cached.data;
      }
    }
    
    const q = query(
      collection(db, 'published'),
      where('contentId', '==', contentId)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.docs.length > 0) {
      const publishedDoc = snapshot.docs[0];
      const data = { id: publishedDoc.id, ...publishedDoc.data() };
      
      // Cache the result
      _publishedCache.set(contentId, { data, timestamp: Date.now() });
      return data;
    }
    return null;
  },

  // Add content to published collection
  addToPublished: async (approvalData) => {
    // Check if document already exists
    const docRef = doc(db, 'published', approvalData.contentId);
    const existingDoc = await getDoc(docRef);
    
    // Calculate read time from bodyHtml
    const readTime = calculateReadTime(approvalData.bodyHtml);
    
    const publishedContent = {
      title: approvalData.title,
      subtitle: approvalData.subtitle,
      bodyHtml: approvalData.bodyHtml,
      socialTitle: approvalData.socialTitle,
      socialDescription: approvalData.socialDescription,
      socialImageUrl: approvalData.socialImageUrl,
      category: approvalData.category,
      tags: approvalData.tags || [],
      minorEligible: approvalData.minorEligible || false,
      author: approvalData.author,
      authorEmail: approvalData.authorEmail,
      authorRole: approvalData.authorRole,
      authorId: approvalData.authorId, // Add authorId to published content
      approvalId: approvalData.id, // Store original approval document ID
      approvedAt: approvalData.approvedAt,
      approvedBy: approvalData.approvedBy,
      contentId: approvalData.contentId,
      status: 'Published',
      readTime: readTime, // Store calculated read time
    };
    
    // If document exists, preserve existing fields like publishedAt and stats
    if (existingDoc.exists()) {
      const existingData = existingDoc.data();
      await setDoc(docRef, {
        ...publishedContent,
        publishedAt: existingData.publishedAt || serverTimestamp(),
        stats: existingData.stats || {
          views: 0,
          shares: 0,
          bookmarks: 0
        },
        updatedAt: serverTimestamp()
      }, { merge: true });
    } else {
      // New document, set all fields including publishedAt and stats
      await setDoc(docRef, {
        ...publishedContent,
        publishedAt: serverTimestamp(),
        stats: {
          views: 0,
          shares: 0,
          bookmarks: 0
        }
      });
    }
    
    return { id: approvalData.contentId, ...publishedContent };
  },

  // Remove from published collection
  removeFromPublished: async (contentId) => {
    await deleteDoc(doc(db, 'published', contentId));
    
    // Invalidate cache for this contentId
    _publishedCache.delete(contentId);
  },
  
  // Clear cache manually (for manual invalidation)
  _clearCache: () => {
    _publishedCache.clear();
  },

  // Update published content
  updatePublished: async (contentId, updateData) => {
    const publishedRef = doc(db, 'published', contentId);
    
    // If bodyHtml is being updated, recalculate readTime
    const dataToUpdate = { ...updateData, updatedAt: serverTimestamp() };
    if (updateData.bodyHtml) {
      dataToUpdate.readTime = calculateReadTime(updateData.bodyHtml);
    }
    
    await updateDoc(publishedRef, dataToUpdate);
    
    // Invalidate cache for this contentId
    _publishedCache.delete(contentId);
  },
};

// Counter Service for generating IDs
export const counterService = {
  // Get counter value
  getCounter: async (counterName) => {
    const counterDoc = await getDoc(doc(db, 'counters', counterName));
    return counterDoc.exists() ? counterDoc.data().value : 0;
  },

  // Increment counter
  incrementCounter: async (counterName) => {
    const counterRef = doc(db, 'counters', counterName);
    const counterDoc = await getDoc(counterRef);
    
    if (!counterDoc.exists()) {
      await setDoc(counterRef, { value: 1 });
      return 1;
    }
    
    const newValue = counterDoc.data().value + 1;
    await updateDoc(counterRef, { value: newValue });
    return newValue;
  }
};

// Utility Functions
export const generateSystemId = async (prefix) => {
  const counter = await counterService.incrementCounter(prefix);
  return `${prefix}${counter.toString().padStart(4, '0')}`;
};

// Notification Service
export const notificationService = {
  // Helper function to check if notification should be delivered based on time-gate logic
  shouldDeliverNotification: (userData, notificationType) => {
    // Check if user has the new notificationSettings structure
    const settings = userData.notificationSettings || {};
    const timestamps = userData.notificationTimestamps || {};
    
    // Map notification types to their settings and timestamp fields
    const typeMapping = {
      'content': {
        settingKey: 'educational',
        timestampKey: 'educationalEnabledAt'
      },
      'appointment': {
        settingKey: 'appointments', 
        timestampKey: 'appointmentsEnabledAt'
      },
      'data_access_request': {
        settingKey: 'dataPrivacy',
        timestampKey: 'dataPrivacyEnabledAt'
      },
      'data_access_termination': {
        settingKey: 'dataPrivacy',
        timestampKey: 'dataPrivacyEnabledAt'
      },
      'announcement': {
        settingKey: 'announcements',
        timestampKey: 'announcementsEnabledAt'
      },
      'event': {
        settingKey: 'events',
        timestampKey: 'eventsEnabledAt'
      }
    };
    
    const mapping = typeMapping[notificationType];
    if (!mapping) {
      // Fallback to old preference system for unknown types
      const prefs = userData.notificationPreferences || {};
      const prefKey = `${notificationType}Notif`;
      return prefs[prefKey] !== false;
    }
    
    // If user doesn't have notificationSettings at all, default to enabling all notifications
    // This ensures backward compatibility with users created before notificationSettings was added
    const hasNotificationSettings = userData.notificationSettings && Object.keys(userData.notificationSettings).length > 0;
    if (!hasNotificationSettings) {
      // User doesn't have new settings structure - use old preference system if available
      const prefs = userData.notificationPreferences || {};
      const prefKey = `${notificationType}Notif`;
      // Default to true if not explicitly disabled in old system
      return prefs[prefKey] !== false;
    }
    
    // Check if notification type is enabled
    const isEnabled = settings[mapping.settingKey] === true;
    if (!isEnabled) {
      return false;
    }
    
    // Check if there's a timestamp checkpoint
    const enabledAt = timestamps[mapping.timestampKey];
    if (!enabledAt) {
      return true; // Allow if no checkpoint exists (backward compatibility)
    }
    
    // Get current time for comparison
    const now = new Date();
    const enabledAtDate = new Date(enabledAt);
    
    // Only deliver if current time is after the checkpoint
    const shouldDeliver = now >= enabledAtDate;
    
    return shouldDeliver;
  },

  // Create notifications for all users who match the content category
  // NOTE: This operation should ideally be moved to a Cloud Function for better scalability
  // Cloud Functions can use server-side filtering and handle large user bases more efficiently
  createNotificationsForPublishedContent: async (contentData) => {
    try {
      const categoryToTopicId = {
        "Menstrual Wellness and Hygiene": 1,
        "Contraception": 2,
        "Fertility Basics": 3,
        "STI Awareness": 4,
        "Mental Health in Reproductive Care": 5,
        "Male Reproductive Health": 6,
        "Pregnancy Prevention": 7,
        "General Reproductive Wellness": 8,
        "Community Outreach Program": 9,
        "Community Outreach Programs": 9,
      };

      const contentCategory = contentData.category;
      const contentTopicId = categoryToTopicId[contentCategory];
      
      // Optimize: Use pagination to process users in batches instead of loading all at once
      const MAX_BATCH_SIZE = 100; // Firestore batch limit is 500 operations
      const USERS_PER_PAGE = 100; // Process users in pages to avoid memory issues
      let lastVisible = null;
      let hasMore = true;
      let totalCount = 0;
      const usersWithEmail = [];

      while (hasMore) {
        // Build query with pagination
        let usersQuery;
        if (lastVisible) {
          usersQuery = query(collection(db, 'users'), startAfter(lastVisible), limit(USERS_PER_PAGE));
        } else {
          usersQuery = query(collection(db, 'users'), limit(USERS_PER_PAGE));
        }

        const usersSnapshot = await getDocs(usersQuery);
        
        if (usersSnapshot.empty) {
          hasMore = false;
          break;
        }

        lastVisible = usersSnapshot.docs[usersSnapshot.docs.length - 1];
        hasMore = usersSnapshot.docs.length === USERS_PER_PAGE;

        const batch = writeBatch(db);
        let batchCount = 0;

        for (const userDoc of usersSnapshot.docs) {
          const userData = userDoc.data();
          const userId = userDoc.id;

          // Skip non-user accounts (staff, admin)
          if (userData.accountType && userData.accountType !== 'User') continue;
          
          // Check if user has content notifications enabled using time-gate logic
          if (!notificationService.shouldDeliverNotification(userData, 'content')) {
            continue;
          }

          // Skip if user has no content preferences
          if (!userData.contentPreferences) continue;

          // Minor eligibility check:
          // If the content is NOT minor-eligible, skip users who are minors.
          const contentIsMinorEligible = contentData.minorEligible === true;
          const userIsMinor = userService.isMinorAccount(userData);
          if (!contentIsMinorEligible && userIsMinor) continue;

          const { selectedTopics = [], topics = [] } = userData.contentPreferences;
          
          // Check for match (either ID or name)
          const isIdMatch = contentTopicId && selectedTopics.includes(contentTopicId);
          const isNameMatch = topics.some(t => t.trim().toLowerCase() === contentCategory.trim().toLowerCase());

          if (isIdMatch || isNameMatch) {
            // Create notification document
            const notifRef = doc(collection(db, 'users', userId, 'notifications'));
            batch.set(notifRef, {
              notificationId: notifRef.id,
              notificationsTitle: contentData.title,
              createdAt: serverTimestamp(),
              status: "unread",
              contentId: contentData.contentId,
              category: contentCategory,
              coverImage: contentData.socialImageUrl || "",
              type: "content"
            });
            batchCount++;
            totalCount++;
            
            // Collect user emails for email notifications
            if (userData.email) {
              usersWithEmail.push({
                email: userData.email,
                userId: userId,
                // Prefer root-level firstName; fall back to nested personalInformation
                firstName: userData.firstName || userData.personalInformation?.firstName || 'User'
              });
            }

            // Check if we've reached batch limit
            if (batchCount >= MAX_BATCH_SIZE) {
              await batch.commit();
              batchCount = 0;
              // Start a new batch for remaining users in this page
            }
          }
        }

        // Commit any remaining operations in this batch
        if (batchCount > 0) {
          await batch.commit();
        }
      }

      if (totalCount > 0) {
        // Send email notifications
        if (usersWithEmail.length > 0) {
          await notificationService.sendPushNotifications(usersWithEmail, contentData);
        }
      }
      
      return totalCount;
    } catch (error) {
      console.error('notificationService: Error creating notifications:', error);
      throw error;
    }
  },

  // Send email notifications to users
  sendPushNotifications: async (users, contentData) => {
    try {
      // Call Firebase Cloud Function to send email notifications
      const functionsUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-auravit-bb843.cloudfunctions.net';
      const response = await fetch(`${functionsUrl}/sendContentEmailNotifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          users,
          contentData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('notificationService: Cloud Function error:', errorData);
        throw new Error(errorData.error || 'Failed to send email notifications');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('notificationService: Error sending email notifications:', error);
      throw error;
    }
  },

  // Get user notifications (real-time listener helper) with time-gate filtering - OPTIMIZED
  getUserNotificationsListener: (userId, callback, limitCount = 50) => {
    const q = query(
      collection(db, 'users', userId, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(limitCount) // Add limit to prevent reading all notifications
    );

    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert timestamp to Date for UI
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));

      // Apply time-gate filtering for display
      const filteredNotifications = notifications.filter(notif => {
        // Always show past notifications (already stored)
        // Only apply time-gate logic to new notifications being created
        // This ensures past notifications remain visible while respecting toggle settings

        // For display purposes, we show all existing notifications
        // The time-gate logic is applied during notification creation
        return true;
      });

      callback(filteredNotifications);
    });
  },

  // Helper function to get filtered notifications for display
  getFilteredNotifications: async (userId) => {
    try {
      const q = query(
        collection(db, 'users', userId, 'notifications'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      // For display, we return all notifications (past ones remain visible)
      // Time-gate logic is applied during creation
      return notifications;
    } catch (error) {
      console.error('Error getting filtered notifications:', error);
      return [];
    }
  },

  // Mark notification as read
  markAsRead: async (userId, notificationId) => {
    try {
      const notifRef = doc(db, 'users', userId, 'notifications', notificationId);
      await updateDoc(notifRef, {
        status: "read"
      });
    } catch (error) {
      console.error('notificationService: Error marking notification as read:', error);
      throw error;
    }
  },

  // Mark multiple notifications as read in a batch
  markMultipleAsRead: async (userId, notificationIds) => {
    try {
      const batch = writeBatch(db);
      notificationIds.forEach(notificationId => {
        const notifRef = doc(db, 'users', userId, 'notifications', notificationId);
        batch.update(notifRef, { status: "read" });
      });
      await batch.commit();
    } catch (error) {
      console.error('notificationService: Error marking multiple notifications as read:', error);
      throw error;
    }
  },

  // Delete notification
  deleteNotification: async (userId, notificationId) => {
    try {
      const notifRef = doc(db, 'users', userId, 'notifications', notificationId);
      await deleteDoc(notifRef);
    } catch (error) {
      console.error('notificationService: Error deleting notification:', error);
      throw error;
    }
  },

  // Create appointment-related notification
  createAppointmentNotification: async (appointmentData) => {
    const { uid, reference, status, serviceName, cancelReason, id: appointmentId } = appointmentData;
    
  
    if (!uid) {
      console.error('[Notification Debug] ERROR: No UID provided in appointment data');
      return;
    }

    try {
      // Fetch user preferences and check time-gate logic
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) {
        console.error('[Notification Debug] ERROR: User document not found for UID:', uid);
        return;
      }
      
      const userData = userDoc.data();
      
      if (!notificationService.shouldDeliverNotification(userData, 'appointment')) {
        return;
      }

      let title = "";
      let message = "";

      // Prioritize Reschedule notification if audit exists
      if (appointmentData.rescheduleAudit) {
        title = "Appointment Rescheduled";
        message = `Your appointment has been rescheduled.
New Date: ${appointmentData.date || 'N/A'}
New Time: ${appointmentData.time || 'N/A'}
Service: ${serviceName || 'N/A'}
Reference: ${reference || 'N/A'}`;
      } else if (status === "PENDING") {
        title = "Appointment Booked";
        message = `Your appointment has been successfully booked.
Service: ${serviceName || 'N/A'}
Date: ${appointmentData.date || 'N/A'}
Time: ${appointmentData.time || 'N/A'}
Reference: ${reference || 'N/A'}`;
      } else if (status === "ACCEPTED") {
        title = "Appointment Accepted";
        message = `Your appointment has been accepted by a Medical Professional.
Service: ${serviceName || 'N/A'}
Date: ${appointmentData.date || 'N/A'}
Time: ${appointmentData.time || 'N/A'}
Reference: ${reference || 'N/A'}`;
      } else if (status === "APPROVED" || status === "CONFIRMED") {
        title = "Appointment Confirmed";
        message = `Your appointment has been confirmed by a Medical Professional.
Service: ${serviceName || 'N/A'}
Date: ${appointmentData.date || 'N/A'}
Time: ${appointmentData.time || 'N/A'}
Reference: ${reference || 'N/A'}`;
      } else if (status === "CANCELLED_BY_PRO" || status === "CANCELLED_BY_USER") {
        title = "Appointment Cancelled";
        const cancelledBy = status === 'CANCELLED_BY_PRO'
          ? (appointmentData.cancelledByRole || 'the Medical Professional')
          : 'you';
        message = `Your appointment has been cancelled by ${cancelledBy}.
Service: ${serviceName || 'N/A'}
Date: ${appointmentData.date || 'N/A'}
Time: ${appointmentData.time || 'N/A'}
${cancelReason ? 'Reason: ' + cancelReason : ''}
${appointmentData.cancelledByEmail ? 'Contact: ' + appointmentData.cancelledByEmail : ''}
Reference: ${reference || 'N/A'}`;
      } else if (status === "COMPLETED") {
        title = "Appointment Completed";
        message = `Your appointment has been completed.
Service: ${serviceName || 'N/A'}
Date: ${appointmentData.date || 'N/A'}
Reference: ${reference || 'N/A'}`;
      }

      if (title && message) {
        // Generate deterministic notification ID for idempotency
        let notificationId;
        if (appointmentData.rescheduleAudit) {
          const rescheduleTimestamp = appointmentData.rescheduleAudit.rescheduledAt?.toMillis() || Date.now();
          notificationId = `${appointmentId}_RESCHEDULED_${rescheduleTimestamp}`;
        } else if (status === 'PENDING') {
          notificationId = `${appointmentId}_BOOKED`;
        } else if (status === 'ACCEPTED') {
          notificationId = `${appointmentId}_ACCEPTED`;
        } else if (status === 'CONFIRMED' || status === 'APPROVED') {
          notificationId = `${appointmentId}_CONFIRMED`;
        } else if (status === 'CANCELLED_BY_PRO' || status === 'CANCELLED_BY_USER') {
          notificationId = `${appointmentId}_CANCELLED`;
        } else if (status === 'COMPLETED') {
          notificationId = `${appointmentId}_COMPLETED`;
        } else {
          // Fallback for any other status
          notificationId = `${appointmentId}_${status}_${Date.now()}`;
        }
        
        
        
        const notifRef = doc(db, 'users', uid, 'notifications', notificationId);
        await setDoc(notifRef, {
          notificationId: notificationId,
          notificationsTitle: title,
          message: message,
          createdAt: serverTimestamp(),
          status: "unread",
          type: "appointment",
          category: "Appointments",
          referenceNumber: reference || "N/A",
          appointmentId: appointmentId || "",
          cancellationReason: status === 'CANCELLED_BY_PRO' || status === 'CANCELLED_BY_USER'
            ? cancelReason || ""
            : "",
          cancelledByRole: appointmentData.cancelledByRole || "",
          cancelledByEmail: appointmentData.cancelledByEmail || ""
        });

        console.log(`[createAppointmentNotification] Created "${title}" notification for user ${uid}, appointmentId: ${appointmentId}`);
      } else {
        console.warn(`[createAppointmentNotification] No title/message generated for status: ${status}, uid: ${uid}`);
      }
    } catch (error) {
      console.error(`[createAppointmentNotification] ERROR writing notification for uid ${uid}, status ${status}:`, error?.code, error?.message, error);
    }
  },

  // Create data access request notification
  createDataAccessRequestNotification: async (uid, professionalId, serviceName, options = {}) => {
    if (!uid) return;

    // Check if this is a professional notification (not for patient)
    const isProfessionalNotification = options.isProfessionalNotification;
    const action = options.action || 'request';

    console.log(`notificationService: Creating data access ${action} notification for ${isProfessionalNotification ? 'professional' : 'user'} ${uid}`);

    try {
      // For professional notifications, check their time-gate logic
      if (!isProfessionalNotification) {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (!userDoc.exists()) return;
        
        const userData = userDoc.data();
        if (!notificationService.shouldDeliverNotification(userData, 'data_access_request')) {
          console.log(`notificationService: Skipping data privacy notification for user ${uid} - disabled or time-gated`);
          return;
        }
      }

      const professionalName = options.professionalName || options.proName || "A Medical Professional";
      let professionalEmail = options.professionalEmail || options.proEmail || "";
      if (!professionalEmail && professionalId) {
        const professionalDoc = await getDoc(doc(db, 'users', professionalId));
        professionalEmail = professionalDoc.exists() ? professionalDoc.data().email || "" : "";
      }
      const requesterLabel = professionalEmail || professionalName;
      const notificationId = options.requestId ? `${options.requestId}_DATA_ACCESS_REQUEST` : doc(collection(db, 'users', uid, 'notifications')).id;
      const notifRef = doc(db, 'users', uid, 'notifications', notificationId);
      
      // Build message based on action type
      let message = '';
      let title = '';
      
      if (isProfessionalNotification) {
        const patientUIC = options.patientUIC || 'N/A';
        const patientName = options.patientName || 'Patient';
        
        if (action === 'denied') {
          title = 'Access Request Denied';
          message = `Patient ${patientName} (${patientUIC}) denied your request to access their data for ${serviceName || 'appointment'}.`;
        } else if (action === 'approved') {
          title = 'Access Request Approved';
          message = `Patient ${patientName} (${patientUIC}) approved your request to access their data for ${serviceName || 'appointment'}.`;
        } else if (action === 'terminated') {
          title = 'Access Terminated';
          message = `Patient ${patientName} (${patientUIC}) terminated your access to their data for ${serviceName || 'appointment'}.`;
        } else {
          title = 'Data Access Request';
          message = `Patient ${patientName} (${patientUIC}) is requesting access to their ${serviceName || 'appointment'} records.`;
        }
      } else {
        title = "Data Access Request";
        message = `${requesterLabel} is requesting access to your ${serviceName || 'appointment'} records.`;
      }

      await setDoc(notifRef, {
        notificationId,
        notificationsTitle: title,
        message: message,
        createdAt: serverTimestamp(),
        status: "unread",
        type: "data_access_request",
        professionalId: professionalId,
        professionalName,
        professionalEmail,
        proEmail: professionalEmail,
        requestId: options.requestId || "",
        patientUIC: options.patientUIC || '',
        patientName: options.patientName || '',
        patientUid: options.patientUid || '',
        appointmentId: options.appointmentId || '',
        category: "Data & Privacy",
        targetPanel: isProfessionalNotification ? "medical_data_access" : "userprofilepanel_data_privacy"
      });
      console.log(`notificationService: Successfully created data access ${action} notification for ${uid}`);
    } catch (error) {
      console.error('notificationService: Error creating data access notification:', error);
    }
  },

  // Create data access termination/expiration notification
  createDataAccessTerminationNotification: async (uid, professionalId, serviceName, isExpired = false, patientUIC = '', patientName = '') => {
    if (!uid) return;

    const actionType = isExpired ? "Expired" : "Terminated";
    console.log(`notificationService: Creating data access ${actionType} notification for user ${uid}`);

    try {
      // Fetch user preferences and check time-gate logic
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) return;
      
      const userData = userDoc.data();
      if (!notificationService.shouldDeliverNotification(userData, 'data_access_termination')) {
        console.log(`notificationService: Skipping data privacy notification for user ${uid} - disabled or time-gated`);
        return;
      }

      const notifRef = doc(collection(db, 'users', uid, 'notifications'));
      await setDoc(notifRef, {
        notificationId: notifRef.id,
        notificationsTitle: `Data Access ${actionType}`,
        message: `The data access for professional (ID: ${professionalId}) regarding your ${serviceName || 'appointment'} records has ${isExpired ? 'expired' : 'been terminated'}.`,
        createdAt: serverTimestamp(),
        status: "unread",
        type: "data_access_termination",
        professionalId: professionalId,
        patientUIC: patientUIC,
        patientName: patientName,
        targetPanel: "userprofilepanel_access_history"
      });
      console.log(`notificationService: Successfully created data access ${actionType} notification for ${uid}`);
    } catch (error) {
      console.error(`notificationService: Error creating data access ${actionType} notification:`, error);
    }
  },

  // Create record update notification (when attachments/findings added to completed appt)
  createRecordUpdateNotification: async (uid, reference, serviceName, appointmentId, patientUIC = '', patientName = '', professionalId = '') => {
    if (!uid) return;

    console.log(`notificationService: Creating record update notification for user ${uid}, ref: ${reference}`);

    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) return;
      
      const userData = userDoc.data();
      if (!notificationService.shouldDeliverNotification(userData, 'appointment')) {
        console.log(`notificationService: Skipping record update notification for user ${uid} - disabled or time-gated`);
        return;
      }

      const notifRef = doc(collection(db, 'users', uid, 'notifications'));
      await setDoc(notifRef, {
        notificationId: notifRef.id,
        notificationsTitle: "Health Record Updated",
        message: `New attachments or clinical findings have been added to your ${serviceName || 'appointment'} record.`,
        createdAt: serverTimestamp(),
        status: "unread",
        type: "appointment",
        referenceNumber: reference || "N/A",
        appointmentId: appointmentId || "",
        patientUIC: patientUIC,
        patientName: patientName,
        professionalId: professionalId,
        targetPanel: "userprofilepanel_records"
      });
      console.log(`notificationService: Successfully created record update notification for ${uid}`);
    } catch (error) {
      console.error('notificationService: Error creating record update notification:', error);
    }
  }
};

export const generateStaffId = async () => {
  const currentYear = new Date().getFullYear();
  const counter = await counterService.incrementCounter('STAFF-AURA-');
  return `AURA${currentYear}${String(counter).padStart(3, '0')}`;
};

export const generateAdminId = async () => {
  return generateSystemId('ADMIN-');
};

// Hybrid Bookmark System - Using Subcollections
export const bookmarkService = {
  // Local cache keys
  LOCAL_BOOKMARKS_KEY: 'userBookmarks',
  PENDING_OPERATIONS_KEY: 'pendingBookmarkOperations',
  LAST_SYNC_KEY: 'lastBookmarkSync',
  
  // Debounce timer
  syncDebounceTimer: null,

  // Initialize bookmark system for a user
  initializeBookmarks: async (userId) => {
    try {
      // Load local cache
      const localBookmarks = bookmarkService.getLocalBookmarks();
      
      // Fetch from database
      const dbBookmarks = await bookmarkService.getDatabaseBookmarks(userId);
      
      // Merge with database as authoritative source
      const mergedBookmarks = bookmarkService.mergeBookmarks(localBookmarks, dbBookmarks);
      
      // Update local cache with merged result
      bookmarkService.setLocalBookmarks(mergedBookmarks);
      
      // Sync pending operations
      await bookmarkService.syncPendingOperations(userId);
      
      return mergedBookmarks;
    } catch (error) {
      console.error('BookmarkService: Error initializing bookmarks:', error);
      return bookmarkService.getLocalBookmarks();
    }
  },

  // Get bookmarks from local cache
  getLocalBookmarks: () => {
    try {
      const stored = localStorage.getItem(bookmarkService.LOCAL_BOOKMARKS_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('BookmarkService: Error reading local bookmarks:', error);
      return {};
    }
  },

  // Set bookmarks in local cache
  setLocalBookmarks: (bookmarks) => {
    try {
      localStorage.setItem(bookmarkService.LOCAL_BOOKMARKS_KEY, JSON.stringify(bookmarks));
    } catch (error) {
      console.error('BookmarkService: Error setting local bookmarks:', error);
    }
  },

  // Get bookmarks from database (subcollection)
  getDatabaseBookmarks: async (userId) => {
    try {
      const bookmarksRef = collection(db, 'users', userId, 'bookmarks');
      const querySnapshot = await getDocs(bookmarksRef);
      
      const bookmarks = {};
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        bookmarks[doc.id] = data.bookmarked || true;
      });
      
      return bookmarks;
    } catch (error) {
      console.error('BookmarkService: Error fetching database bookmarks from subcollection:', error);
      return {};
    }
  },

  // Merge local and database bookmarks (database is authoritative)
  mergeBookmarks: (localBookmarks, dbBookmarks) => {
    return dbBookmarks;
  },

  // Optimistic bookmark toggle with debounced sync
  toggleBookmark: async (userId, itemId, title) => {
    const currentBookmarks = bookmarkService.getLocalBookmarks();
    const isCurrentlyBookmarked = currentBookmarks[itemId] || false;
    
    // Optimistic UI update - update local cache immediately
    const updatedBookmarks = {
      ...currentBookmarks,
      [itemId]: !isCurrentlyBookmarked
    };
    bookmarkService.setLocalBookmarks(updatedBookmarks);
    
    // Queue operation for database sync
    const operation = {
      type: !isCurrentlyBookmarked ? 'add' : 'remove',
      itemId,
      title,
      timestamp: Date.now()
    };
    
    // Add to pending operations
    bookmarkService.addPendingOperation(operation);
    
    // Debounced sync - clear existing timer and set new one
    if (bookmarkService.syncDebounceTimer) {
      clearTimeout(bookmarkService.syncDebounceTimer);
    }
    
    bookmarkService.syncDebounceTimer = setTimeout(async () => {
      try {
        await bookmarkService.syncPendingOperations(userId);
      } catch (error) {
        console.error('BookmarkService: Error in debounced sync:', error);
      }
    }, 1000); // 1 second debounce
    
    return !isCurrentlyBookmarked;
  },

  // Get pending operations
  getPendingOperations: () => {
    try {
      const stored = localStorage.getItem(bookmarkService.PENDING_OPERATIONS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('BookmarkService: Error reading pending operations:', error);
      return [];
    }
  },

  // Add pending operation
  addPendingOperation: (operation) => {
    const pending = bookmarkService.getPendingOperations();
    pending.push(operation);
    localStorage.setItem(bookmarkService.PENDING_OPERATIONS_KEY, JSON.stringify(pending));
  },

  // Clear pending operations
  clearPendingOperations: () => {
    localStorage.removeItem(bookmarkService.PENDING_OPERATIONS_KEY);
  },

  // Sync pending operations to database (subcollection) - optimized with batch operations
  syncPendingOperations: async (userId) => {
    const pending = bookmarkService.getPendingOperations();
    if (pending.length === 0) {
      return;
    }

    try {
      // Limit batch size to prevent excessively large write bursts (Firestore limit is 500 operations)
      const MAX_BATCH_SIZE = 100;
      const operationsToProcess = pending.slice(0, MAX_BATCH_SIZE);
      const remainingOperations = pending.slice(MAX_BATCH_SIZE);
      
      // Use batch operation for efficiency
      const batch = writeBatch(db);
      
      // Process operations in order using subcollection
      for (const operation of operationsToProcess) {
        const { itemId, type, title } = operation;
        
        if (type === 'add') {
          // Add bookmark document to subcollection
          const bookmarkRef = doc(db, 'users', userId, 'bookmarks', itemId);
          const bookmarkData = {
            bookmarked: true,
            createdAt: serverTimestamp()
          };
          
          if (title) {
            bookmarkData.title = title;
          }
          
          batch.set(bookmarkRef, bookmarkData);
          
        } else if (type === 'remove') {
          // Remove bookmark document from subcollection
          const bookmarkRef = doc(db, 'users', userId, 'bookmarks', itemId);
          batch.delete(bookmarkRef);
        }
      }
      
      // Commit batch
      await batch.commit();
      
      // Update pending operations to remove processed ones
      if (remainingOperations.length > 0) {
        localStorage.setItem(bookmarkService.PENDING_OPERATIONS_KEY, JSON.stringify(remainingOperations));
      } else {
        bookmarkService.clearPendingOperations();
      }
      
      // Update last sync timestamp
      localStorage.setItem(bookmarkService.LAST_SYNC_KEY, Date.now().toString());
      
    } catch (error) {
      console.error('BookmarkService: Error syncing pending operations:', error);
      // Keep operations in queue for retry
    }
  },

  // Check if item is bookmarked
  isBookmarked: (itemId) => {
    const bookmarks = bookmarkService.getLocalBookmarks();
    return bookmarks[itemId] || false;
  },

  // Get all bookmarked items
  getBookmarkedItems: () => {
    const bookmarks = bookmarkService.getLocalBookmarks();
    return Object.keys(bookmarks).filter(itemId => bookmarks[itemId]);
  }
};

// Helper function to calculate read time based on word count
const calculateReadTime = (content) => {
  if (!content || typeof content !== 'string') {
    return 1; // Default to 1 minute for empty/invalid content
  }

  // Strip HTML tags if content contains HTML
  const textContent = content.replace(/<[^>]+>/g, '');
  
  // Trim and split by whitespace to count words
  const words = textContent.trim().split(/\s+/);
  const wordCount = words.length;
  
  // Calculate read time using 200 words per minute, always round up
  const readTime = Math.ceil(wordCount / 200);
  
  // Minimum 1 minute
  return Math.max(1, readTime);
};

// Stats Service for Published Content Metrics
const _isRegularUserCache = new Map();
const _viewedArticlesThisSession = new Set();
let _viewedArticlesCacheCleanupTimer = null;

export const statsService = {
  _clearCaches: () => {
    _isRegularUserCache.clear();
    _viewedArticlesThisSession.clear();
  },

  // Helper to get date string in YYYY-MM-DD format
  getDateString: (date = new Date()) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  },

  // Initialize stats for a published content item - optimized to use setDoc with merge
  initializeStats: async (contentId) => {
    const publishedRef = doc(db, 'published', contentId);
    
    // Use setDoc with merge to avoid read - this will only set stats if they don't exist
    await setDoc(publishedRef, {
      stats: {
        views: 0,
        shares: 0,
        bookmarks: 0
      }
    }, { merge: true });
  },

  // Helper to increment global daily analytics - optimized to use setDoc with merge
  incrementDailyAnalytics: async (metric) => {
    try {
      const dateStr = statsService.getDateString();
      const analyticsRef = doc(db, 'analytics', dateStr);
      
      // Use setDoc with merge to avoid read
      await setDoc(analyticsRef, {
        views: 0,
        shares: 0,
        bookmarks: 0,
        [metric]: increment(1)
      }, { merge: true });
    } catch (error) {
      console.error('statsService: Error incrementing daily analytics:', error);
      throw error;
    }
  },
  
  // Helper to increment author-specific daily analytics - optimized to use setDoc with merge
  incrementAuthorDailyAnalytics: async (authorId, metric) => {
    try {
      const dateStr = statsService.getDateString();
      const authorAnalyticsRef = doc(db, 'analytics', dateStr, 'authors', authorId);
      
      // Use setDoc with merge to avoid read
      await setDoc(authorAnalyticsRef, {
        views: 0,
        shares: 0,
        bookmarks: 0,
        [metric]: increment(1)
      }, { merge: true });
    } catch (error) {
      console.error('statsService: Error incrementing author daily analytics:', error);
      throw error;
    }
  },

  // Helper to check if user is a regular user (not staff/admin)
  // OPTIMIZED: Session-level cache avoids repeated Firestore reads per action
  isRegularUser: async (userId, cachedUserData = null) => {
    if (!userId) {
      return false;
    }

    if (cachedUserData) {
      const accountType = cachedUserData.accountType;
      const excludedRoles = ['Admin', 'Staff', 'Content Curator', 'Peer Educator', 'Medical Professional'];
      const isExcluded = accountType ? excludedRoles.includes(accountType) : false;
      return !isExcluded;
    }

    if (_isRegularUserCache.has(userId)) {
      return _isRegularUserCache.get(userId);
    }

    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      _isRegularUserCache.set(userId, false);
      return false;
    }
    const accountType = userDoc.data()?.accountType;
    const excludedRoles = ['Admin', 'Staff', 'Content Curator', 'Peer Educator', 'Medical Professional'];
    const isExcluded = accountType ? excludedRoles.includes(accountType) : false;
    const result = !isExcluded;
    _isRegularUserCache.set(userId, result);
    return result;
  },

  _hasViewedThisSession: (contentId, userId) => {
    const key = `${userId}::${contentId}`;
    const hasViewed = _viewedArticlesThisSession.has(key);
    if (!hasViewed) {
      _viewedArticlesThisSession.add(key);
      if (!_viewedArticlesCacheCleanupTimer) {
        _viewedArticlesCacheCleanupTimer = setTimeout(() => {
          _viewedArticlesThisSession.clear();
          _viewedArticlesCacheCleanupTimer = null;
        }, 30 * 60 * 1000);
      }
    }
    return hasViewed;
  },

  // Increment view counter - optimized with batch operations
  // OPTIMIZED: Deduplicates views per user+content per session; uses cached user+published data
  incrementViews: async (contentId, userId, opts = {}) => {
    try {
      const { cachedUserData = null, cachedPublishedDoc = null, skipDedupe = false } = opts;

      if (!skipDedupe && statsService._hasViewedThisSession(contentId, userId)) {
        return;
      }

      const isRegular = await statsService.isRegularUser(userId, cachedUserData);
      if (!isRegular) {
        return;
      }
      
      const publishedRef = doc(db, 'published', contentId);
      let publishedDoc = cachedPublishedDoc;
      if (!publishedDoc) {
        const snap = await getDoc(publishedRef);
        publishedDoc = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      }
      
      if (!publishedDoc) {
        return;
      }
      
      const authorId = publishedDoc.authorId;

      const batch = writeBatch(db);

      // Use atomic field update instead of full object with merge
      batch.update(publishedRef, {
        'stats.views': increment(1)
      });

      const dateStr = statsService.getDateString();
      const analyticsRef = doc(db, 'analytics', dateStr);
      batch.set(analyticsRef, { views: increment(1) }, { merge: true });

      if (authorId) {
        const authorAnalyticsRef = doc(db, 'analytics', dateStr, 'authors', authorId);
        batch.set(authorAnalyticsRef, { views: increment(1) }, { merge: true });
      }
      
      await batch.commit();
    } catch (error) {
      console.error('statsService: Error incrementing views:', error);
      throw error;
    }
  },

  // Increment share counter - optimized with batch operations
  // OPTIMIZED: Uses cached user data when available
  incrementShares: async (contentId, userId, opts = {}) => {
    try {
      const { cachedUserData = null, cachedPublishedDoc = null } = opts;

      const isRegular = await statsService.isRegularUser(userId, cachedUserData);
      if (!isRegular) {
        return;
      }
      
      const publishedRef = doc(db, 'published', contentId);
      let publishedDoc = cachedPublishedDoc;
      if (!publishedDoc) {
        const snap = await getDoc(publishedRef);
        publishedDoc = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      }
      
      if (!publishedDoc) {
        return;
      }
      
      const authorId = publishedDoc.authorId;

      const batch = writeBatch(db);

      // Use atomic field update instead of full object with merge
      batch.update(publishedRef, {
        'stats.shares': increment(1)
      });

      const dateStr = statsService.getDateString();
      const analyticsRef = doc(db, 'analytics', dateStr);
      batch.set(analyticsRef, { shares: increment(1) }, { merge: true });

      if (authorId) {
        const authorAnalyticsRef = doc(db, 'analytics', dateStr, 'authors', authorId);
        batch.set(authorAnalyticsRef, { shares: increment(1) }, { merge: true });
      }
      
      await batch.commit();
    } catch (error) {
      console.error('statsService: Error incrementing shares:', error);
      throw error;
    }
  },

  // Increment bookmark counter - optimized with batch operations
  // OPTIMIZED: Uses cached user data when available
  incrementBookmarks: async (contentId, userId, opts = {}) => {
    try {
      const { cachedUserData = null, cachedPublishedDoc = null } = opts;

      const isRegular = await statsService.isRegularUser(userId, cachedUserData);
      if (!isRegular) {
        return;
      }
      
      const publishedRef = doc(db, 'published', contentId);
      let publishedDoc = cachedPublishedDoc;
      if (!publishedDoc) {
        const snap = await getDoc(publishedRef);
        publishedDoc = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      }
      
      if (!publishedDoc) {
        return;
      }
      
      const authorId = publishedDoc.authorId;

      const batch = writeBatch(db);

      // Use atomic field update instead of full object with merge
      batch.update(publishedRef, {
        'stats.bookmarks': increment(1)
      });

      const dateStr = statsService.getDateString();
      const analyticsRef = doc(db, 'analytics', dateStr);
      batch.set(analyticsRef, { bookmarks: increment(1) }, { merge: true });

      if (authorId) {
        const authorAnalyticsRef = doc(db, 'analytics', dateStr, 'authors', authorId);
        batch.set(authorAnalyticsRef, { bookmarks: increment(1) }, { merge: true });
      }
      
      await batch.commit();
    } catch (error) {
      console.error('statsService: Error incrementing bookmarks:', error);
      throw error;
    }
  },

  // Decrement bookmark counter (never goes below 0) - optimized with batch operations
  // OPTIMIZED: Uses cached user data when available
  decrementBookmarks: async (contentId, userId, opts = {}) => {
    try {
      const { cachedUserData = null, cachedPublishedDoc = null } = opts;

      const isRegular = await statsService.isRegularUser(userId, cachedUserData);
      if (!isRegular) {
        return;
      }
      
      const publishedRef = doc(db, 'published', contentId);
      let publishedDoc = cachedPublishedDoc;
      if (!publishedDoc) {
        const snap = await getDoc(publishedRef);
        publishedDoc = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      }
      
      if (publishedDoc) {
        const authorId = publishedDoc.authorId;
        const existingStats = publishedDoc.stats || { views: 0, shares: 0, bookmarks: 0 };
        const currentBookmarks = existingStats.bookmarks || 0;

        if (currentBookmarks > 0) {
          const batch = writeBatch(db);

          // Use atomic field update instead of full object with merge
          batch.update(publishedRef, {
            'stats.bookmarks': increment(-1)
          });

          const dateStr = statsService.getDateString();
          const analyticsRef = doc(db, 'analytics', dateStr);
          batch.set(analyticsRef, { bookmarks: increment(-1) }, { merge: true });

          if (authorId) {
            const authorAnalyticsRef = doc(db, 'analytics', dateStr, 'authors', authorId);
            batch.set(authorAnalyticsRef, { bookmarks: increment(-1) }, { merge: true });
          }

          await batch.commit();
        }
      }
    } catch (error) {
      console.error('statsService: Error decrementing bookmarks:', error);
      throw error;
    }
  },

  // Get stats for a content item
  getStats: async (contentId) => {
    const publishedDoc = await getDoc(doc(db, 'published', contentId));
    
    if (publishedDoc.exists()) {
      const stats = publishedDoc.data()?.stats || {
        views: 0,
        shares: 0,
        bookmarks: 0
      };
      return stats;
    }
    
    return {
      views: 0,
      shares: 0,
      bookmarks: 0
    };
  },

  // Get daily analytics for the last N days (global or author-specific)
  getDailyAnalytics: async (days = 7, authorId = null) => {
    const dates = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dates.push(statsService.getDateString(d));
    }

    const statsPromises = dates.map(async (dateStr) => {
      if (authorId) {
        // Fetch author-specific analytics
        const docRef = doc(db, 'analytics', dateStr, 'authors', authorId);
        const docSnap = await getDoc(docRef);
        return {
          date: dateStr,
          ...(docSnap.exists() ? docSnap.data() : { views: 0, shares: 0, bookmarks: 0 })
        };
      } else {
        // Fetch global analytics
        const docRef = doc(db, 'analytics', dateStr);
        const docSnap = await getDoc(docRef);
        return {
          date: dateStr,
          ...(docSnap.exists() ? docSnap.data() : { views: 0, shares: 0, bookmarks: 0 })
        };
      }
    });

    const results = await Promise.all(statsPromises);
    return results;
  }
};

// Drafts Management
export const draftsService = {
  // Create a new draft
  createDraft: async (draftData) => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const draftWithMetadata = {
      ...draftData,
      authorId: user.uid,
      authorEmail: user.email,
      author: user.displayName || user.email,
      authorRole: 'Content Curator',
      status: 'draft',
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'drafts'), draftWithMetadata);
    return { id: docRef.id, ...draftWithMetadata };
  },

  // Get a draft by ID
  getDraftById: async (draftId) => {
    const draftDoc = await getDoc(doc(db, 'drafts', draftId));
    return draftDoc.exists() ? { id: draftDoc.id, ...draftDoc.data() } : null;
  },

  // Get all drafts for current user
  getUserDrafts: async () => {
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    try {
      // Matching the user's index: authorId, status, updatedAt
      const q = query(
        collection(db, 'drafts'),
        where('authorId', '==', user.uid),
        where('status', '==', 'draft'),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('draftsService.getUserDrafts: Error executing query:', error);
      
      // Fallback: Try without status filter if index is missing or different
      try {
        const fallbackQ = query(
          collection(db, 'drafts'),
          where('authorId', '==', user.uid),
          orderBy('updatedAt', 'desc')
        );
        const fallbackSnapshot = await getDocs(fallbackQ);
        return fallbackSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (fallbackError) {
         console.error('draftsService.getUserDrafts: Fallback query also failed:', fallbackError);
         
         // Final fallback: No ordering, just filter by authorId (simple query)
         try {
           const simpleQ = query(
             collection(db, 'drafts'),
             where('authorId', '==', user.uid)
           );
           const simpleSnapshot = await getDocs(simpleQ);
           return simpleSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
         } catch (simpleError) {
           console.error('draftsService.getUserDrafts: Simple query also failed:', simpleError);
           throw simpleError;
         }
       }
    }
  },

  // Get paginated user drafts with lazy loading
  getPaginatedUserDrafts: async (pageSize = 100, lastDoc = null) => {
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      let q = query(
        collection(db, 'drafts'),
        where('authorId', '==', user.uid),
        where('status', '==', 'draft'),
        orderBy('updatedAt', 'desc'),
        limit(pageSize)
      );
      
      if (lastDoc) {
        q = query(
          collection(db, 'drafts'),
          where('authorId', '==', user.uid),
          where('status', '==', 'draft'),
          orderBy('updatedAt', 'desc'),
          startAfter(lastDoc),
          limit(pageSize)
        );
      }
      
      const snapshot = await getDocs(q);
      const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const lastVisible = snapshot.docs[snapshot.docs.length - 1];
      
      return {
        data: result,
        lastDoc: lastVisible,
        hasMore: result.length === pageSize
      };
    } catch (error) {
      console.error('getPaginatedUserDrafts: Error fetching paginated drafts:', error);
      throw error;
    }
  },

  // Update a draft
  updateDraft: async (draftId, updateData) => {
    const draftRef = doc(db, 'drafts', draftId);
    await updateDoc(draftRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    
    // Return updated draft
    return await draftsService.getDraftById(draftId);
  },

  // Delete a draft
  deleteDraft: async (draftId) => {
    await deleteDoc(doc(db, 'drafts', draftId));
  }
};

// Revision Service
export const revisionService = {
  // Get all revisions
  getAllRevisions: async () => {
    const q = query(
      collection(db, 'revisions'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Get paginated revisions with lazy loading
  getPaginatedRevisions: async (pageSize = 100, lastDoc = null) => {
    try {
      let q = query(
        collection(db, 'revisions'),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
      
      if (lastDoc) {
        q = query(
          collection(db, 'revisions'),
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(pageSize)
        );
      }
      
      const snapshot = await getDocs(q);
      const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const lastVisible = snapshot.docs[snapshot.docs.length - 1];
      
      return {
        data: result,
        lastDoc: lastVisible,
        hasMore: result.length === pageSize
      };
    } catch (error) {
      console.error('getPaginatedRevisions: Error fetching paginated revisions:', error);
      throw error;
    }
  },

  // Get revision by contentId
  getRevisionByContentId: async (contentId) => {
    // Check cache first
    if (_revisionCache.has(contentId)) {
      const cached = _revisionCache.get(contentId);
      if (Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data;
      }
    }
    
    const q = query(
      collection(db, 'revisions'),
      where('contentId', '==', contentId)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.docs.length > 0) {
      const revisionDoc = snapshot.docs[0];
      const data = { id: revisionDoc.id, ...revisionDoc.data() };
      
      // Cache the result
      _revisionCache.set(contentId, { data, timestamp: Date.now() });
      return data;
    }
    
    return null;
  },

  // Create a new revision
  createRevision: async (revisionData) => {
    const docRef = await addDoc(collection(db, 'revisions'), {
      ...revisionData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { id: docRef.id, ...revisionData };
  },

  // Update an existing revision
  updateRevision: async (revisionId, updateData) => {
    const revisionRef = doc(db, 'revisions', revisionId);
    const data = {
      ...updateData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(revisionRef, data);
    
    // Invalidate cache for this contentId
    if (updateData.contentId) {
      _revisionCache.delete(updateData.contentId);
    }
    
    return { id: revisionId, ...data };
  },

  // Delete a revision
  deleteRevision: async (revisionId) => {
    await deleteDoc(doc(db, 'revisions', revisionId));
    
    // Clear entire cache when revision is deleted
    _revisionCache.clear();
  },
  
  // Clear cache manually (for manual invalidation)
  _clearCache: () => {
    _revisionCache.clear();
  }
};
